<template>
    <div class="btn-export-v2" v-click-outside="close">
        <BmvButton
            variant="secondary"
            :icon="'fal fa-file-export'"
            :icon-right="open ? 'fal fa-chevron-up' : 'fal fa-chevron-down'"
            :loading="loading"
            size="sm"
            @click="open = !open"
        >
            <span class="btn-export-v2-label">{{ $t('topbar.export') || 'Export' }}</span>
        </BmvButton>
        <div v-if="open" class="btn-export-v2-menu" role="menu">
            <button type="button" class="btn-export-v2-item" @click="exportAll">
                <i class="fal fa-download"></i>
                <span>{{ $t('post.page.export.all') }}</span>
            </button>
            <button
                v-if="hasFilters"
                type="button"
                class="btn-export-v2-item"
                @click="exportFiltered"
            >
                <i class="fal fa-filter"></i>
                <span>{{ $t('post.page.export.filtered') }}</span>
            </button>
        </div>
    </div>
</template>

<script>
/**
 * SPDX-FileCopyrightText: © 2021-2026 SAS Cœur du Web, Paris (France) — https://coeurduweb.com
 * SPDX-License-Identifier: LicenseRef-CoeurDuWeb-Proprietary
 *
 * Tous droits réservés. Logiciel propriétaire, non libre.
 * Les conditions d'utilisation, le périmètre de la licence concédée et les
 * restrictions applicables figurent dans le fichier LICENCE.md à la racine du dépôt.
 */
import BmvButton from '@/components/UI/v2/BmvButton.vue'
import { mapActions } from 'vuex'

const MIME_TYPES = {
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv'
}

export default {
    name: 'ButtonExportPostsV2',
    components: { BmvButton },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => {
                    if (!el.contains(e.target)) binding.value()
                }
                document.addEventListener('mousedown', el._clickOutside)
            },
            unbind(el) {
                document.removeEventListener('mousedown', el._clickOutside)
            }
        }
    },
    data() {
        return { open: false, loading: false }
    },
    computed: {
        hasFilters() {
            // Only show "filtered export" when we're on a Posts-like route
            return /\/posts(\/|$|\?)/.test(this.$route.path) || /\/kanban/.test(this.$route.path)
        }
    },
    methods: {
        ...mapActions('post', ['getExportPosts']),
        close() {
            this.open = false
        },
        exportAll() {
            this.close()
            this.runExport({ filters: {} })
        },
        exportFiltered() {
            this.close()
            this.runExport()
        },
        runExport(filter) {
            if (this.loading) return
            this.loading = true
            this.getExportPosts(filter)
                .then((response) => {
                    const payload = response && response.data
                    if (!payload || !payload.content) return
                    this.saveFile(payload.name || 'posts.xlsx', payload.content)
                })
                .catch((e) => this.$store.dispatch('dispatchError', e))
                .finally(() => {
                    this.loading = false
                })
        },
        saveFile(name, content) {
            const base64 = String(content).replace(/^data:[^;]*;base64,/, '')
            const binary = window.atob(base64)
            const bytes = new Uint8Array(binary.length)
            for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i)
            }
            const ext = (name.split('.').pop() || '').toLowerCase()
            const blob = new Blob([bytes], {
                type: MIME_TYPES[ext] || 'application/octet-stream'
            })
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = name
            document.body.appendChild(link)
            link.click()
            link.remove()
            window.URL.revokeObjectURL(url)
        }
    }
}
</script>

<style scoped>
.btn-export-v2 {
    position: relative;
}

.btn-export-v2-label {
    display: none;
}

@media (min-width: 640px) {
    .btn-export-v2-label {
        display: inline;
    }
}

.btn-export-v2-menu {
    position: absolute;
    top: calc(100% + 6px);
    right: 0;
    min-width: 220px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    box-shadow: var(--v2-shadow-lg);
    padding: 4px;
    z-index: 40;
    font-family: var(--v2-font-sans);
}

.btn-export-v2-item {
    display: flex;
    align-items: center;
    gap: var(--v2-space-3);
    width: 100%;
    padding: 8px var(--v2-space-3);
    border: none;
    background: transparent;
    border-radius: var(--v2-radius-sm);
    color: var(--v2-text-2);
    font-size: var(--v2-text-sm-size);
    text-align: left;
    cursor: pointer;
    font-family: var(--v2-font-sans);
    transition: background var(--v2-transition-fast),
        color var(--v2-transition-fast);
}

.btn-export-v2-item:hover {
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
}

.btn-export-v2-item i {
    width: 18px;
    font-size: 0.9rem;
    flex-shrink: 0;
    color: var(--v2-text-4);
}

.btn-export-v2-item:hover i {
    color: var(--v2-primary);
}
</style>
